import { useMemo } from 'react'
import type { AppDispatch } from '@redux/store'

import type { EntityOptions } from '@redux/modules/module'

import { matchFilterKey, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'
import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import * as serviceProductActions from '@redux/modules/serviceProduct'

import matchFilterArrayIncludesArrayItem from '@functions/matchFilterArrayIncludesArrayItem'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { ServiceProductModel } from '@domain-group/realhub-backend-api-types'

const watchEntityKeys = ['serviceProducts']

type LoadServiceProductIdsParams = {
  dispatch: AppDispatch,
  serviceProductIds: number[],
  requestOptions?: EntityOptions,
}

const loadServiceProductIds = (params: LoadServiceProductIdsParams) => {
  const { dispatch, serviceProductIds, requestOptions } = params
  const { loadServiceProductIds: loadFn } = serviceProductActions

  return dispatch(loadFn(serviceProductIds, requestOptions))
}

type ServiceProductFilters = {
  provider_ids?: number[],
  service_ids?: number[],
  service_id?: number,
  string?: string,
}

type UseServiceProductsOptions = {
  filters?: ServiceProductFilters,
  performHttpRequests?: boolean,
  requestOptions?: EntityOptions,
}

function useServiceProducts(options: UseServiceProductsOptions) {
  const { filters = {}, requestOptions } = options
  const {
    provider_ids: filterProviderIds,
    service_ids: filterServiceIds,
    service_id: filterServiceId,
    string: filterString,
  } = filters

  const {
    updatedEntities: { serviceProducts: serviceProductsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const dispatch = useDispatch()

  const { serviceProducts } = useSelector(reduxState => reduxState.entities)

  // Filter redux entities with provided filters
  const filteredServiceProducts = useMemo(() => {
    const filtered = Object.values(serviceProducts).filter((serviceProduct: ServiceProductModel) => {
      const {
        id, provider_ids = [], service_id, title,
      } = serviceProduct

      const matchId = id?.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle

      const matchProviderIds = !provider_ids?.length
        ? false
        // @ts-expect-error TS2345
        : matchFilterArrayIncludesArrayItem(provider_ids, filterProviderIds)

      const matchServiceId = Array.isArray(filterServiceIds) && filterServiceIds.length
        ? filterServiceIds.includes(service_id ?? -1)
        // @ts-expect-error TS2345
        : matchFilterKey(service_id, filterServiceId)

      return matchProviderIds && matchServiceId && matchString
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [serviceProductsUpdatedAt, JSON.stringify(filters)])

  const filteredServiceProductsCount = filteredServiceProducts.length
  const hasFilteredServiceProducts = !!filteredServiceProductsCount

  // To be used in conjunction with DashboardModule.LoadMoreFooter
  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredServiceProductsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  // Load entities when performHttpRequests is true
  const reduxActionPayload = useReduxAction(
    'serviceProducts',
    'loadServiceProducts',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }) => performHttpRequests && !loading,
    },
  )

  const { loading: loadingServiceProducts, reduxActionPerformed } = reduxActionPayload

  return {
    callbacks: {
      loadMore,
      loadServiceProductIds: (params: { serviceProductIds: number[], entityOptions?: EntityOptions }) => {
        const { serviceProductIds, entityOptions } = params
        return loadServiceProductIds({ dispatch, serviceProductIds, requestOptions: entityOptions })
      },
    },
    canLoadMore,
    filteredServiceProducts,
    filteredServiceProductsCount,
    hasFilteredServiceProducts,
    loading: loadingServiceProducts,
    reduxActionPerformed,
    serviceProducts,
  }
}

export default useServiceProducts
